<script setup lang="ts">
// Server settings for admins: the server's name, who may create spaces (and how many),
// the attachment and repository size limits, fonts, where attachments are kept, and every account on this server with its
// admin and space-creation flags.
import { computed, onMounted, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { describeError } from '../api/http'
import type { LocalUser, ServerSettings, SpaceCreation } from '../api/types'
import { filterPeople } from '../lib/people'
import { forTheme, readableOn } from '../lib/color'
import { useSession } from '../stores/session'
import AttachmentSettings from '../components/AttachmentSettings.vue'
import FontSettings from '../components/FontSettings.vue'
import UpdateSettings from '../components/UpdateSettings.vue'
import AdminReports from '../components/AdminReports.vue'

const session = useSession()
const saved = ref<ServerSettings | null>(null)
const form = reactive<ServerSettings>({
  name: '',
  space_creation: 'approved',
  max_spaces_per_user: 10,
  palette: [],
  profile_photos: true,
  push_notifications: true,
  max_attachment_mb: 100,
  attachment_ceiling_mb: 100,
  max_repository_mb: 2048,
  max_repositories_total_mb: 0,
  max_party_size: 25,
  large_space_members: 0,
  member_storage: false,
  max_locations_per_user: 2,
  max_libraries_per_user: 5,
})
const newColor = ref('#5a6dff')
/** Where the new colour lacks 3:1 contrast, and so is drawn adjusted (forTheme). */
const newAdjusted = computed(() => {
  const c = newColor.value.toLowerCase()
  return { dark: !readableOn(c, true), light: !readableOn(c, false) }
})
/** Swatch halves: how a colour is drawn on the dark and on the light theme. */
const swatch = (c: string) => ({ background: `linear-gradient(135deg, ${forTheme(c, true)} 50%, ${forTheme(c, false)} 50%)` })
/** Adds a colour; any will do, as it's drawn with 3:1 contrast on each theme. */
function addColor(): void {
  const c = newColor.value.toLowerCase()
  if (!form.palette.includes(c) && form.palette.length < 16) form.palette = [...form.palette, c]
}
function removeColor(c: string): void {
  if (form.palette.length > 2) form.palette = form.palette.filter((x) => x !== c)
}
const users = ref<LocalUser[]>([])
const router = useRouter()
const tab = ref<'settings' | 'moderation'>('settings')
const query = ref('')
const busy = ref(false)

const shown = computed(() => filterPeople(users.value, query.value))
const dirty = computed(() => !!saved.value && JSON.stringify(saved.value) !== JSON.stringify({ ...form, palette: [...form.palette] }))
const policies: { value: SpaceCreation; label: string; hint: string }[] = [
  { value: 'approved', label: 'Admins and approved people', hint: 'Tick “Create spaces” for someone below to let them' },
  { value: 'everyone', label: 'Everyone', hint: 'Any account on this server' },
]

const pad = (n: number, w = 3) => String(n).padStart(w, '0')

async function load(): Promise<void> {
  try {
    const [s, u] = await Promise.all([session.api.serverSettings(), session.api.localUsers()])
    saved.value = s
    Object.assign(form, s)
    users.value = u
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
}

async function run(fn: () => Promise<void>, ok?: string): Promise<void> {
  busy.value = true
  try {
    await fn()
    if (ok) session.toast(ok)
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = false
  }
}

const save = () =>
  run(async () => {
    const s = await session.api.updateServerSettings({
      ...form,
      palette: [...form.palette],
      max_spaces_per_user: Number(form.max_spaces_per_user) || 0,
      max_attachment_mb: Number(form.max_attachment_mb) || 0,
      max_repository_mb: Number(form.max_repository_mb) || 0,
      max_repositories_total_mb: Number(form.max_repositories_total_mb) || 0,
      max_party_size: Number(form.max_party_size) || 0,
      large_space_members: Number(form.large_space_members) || 0,
      max_locations_per_user: Number(form.max_locations_per_user) || 0,
      max_libraries_per_user: Number(form.max_libraries_per_user) || 0,
    })
    saved.value = s
    Object.assign(form, s)
    await Promise.all([session.loadMe(), session.loadInstance()])
  }, 'Server settings saved')

const setFlag = (u: LocalUser, flag: 'is_admin' | 'can_create_spaces', on: boolean) =>
  run(async () => {
    if (flag === 'is_admin' && on && !confirm(`Make ${u.user_id} a server admin? They can change these settings and see every library.`)) {
      users.value = [...users.value]
      return
    }
    await session.api.updateAccount(u.user_id, { [flag]: on })
    users.value = await session.api.localUsers()
  })

onMounted(load)
</script>

<template>
  <main class="scroll-thin min-w-0 flex-1 overflow-y-auto bg-bg-tertiary" data-testid="server-admin">
    <header class="border-b border-border px-6 pt-6 max-md:px-4 max-md:pt-3">
      <!-- Phones hide the rail here, like an open library: Back returns to it -->
      <button class="-ml-3 mb-1 flex h-10 items-center gap-1 pr-2 text-text-secondary hover:text-text-primary md:hidden" data-testid="server-back" @click="router.push({ name: 'home' })">
        <svg viewBox="0 0 16 16" class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M10 3L5 8l5 5" />
        </svg>
        <span class="mono uppercase">Back</span>
      </button>
      <div class="mono mb-2 flex flex-wrap gap-x-4 text-text-muted uppercase">
        <span>Server</span>
        <span>{{ session.me?.split('@')[1] }}</span>
        <span>admin</span>
      </div>
      <h1 class="pb-3 text-4xl font-light tracking-tight max-md:text-3xl" data-testid="server-name">{{ saved?.name ?? '…' }}</h1>
      <!-- Counts in one line, like a library's -->
      <p class="mono pb-4 text-text-muted uppercase" data-testid="server-counts">
        {{ pad(users.length) }} accounts · {{ pad(users.filter((u) => u.is_admin).length) }} admins ·
        {{ pad(users.reduce((n, u) => n + u.spaces_owned, 0)) }} spaces
      </p>
      <nav class="mono -mx-6 flex gap-1 border-t border-border px-6 max-md:-mx-4 max-md:px-4" role="tablist">
        <button
          v-for="t in (['settings', 'moderation'] as const)"
          :key="t"
          class="-mb-px border-b px-3 py-2 uppercase"
          :class="tab === t ? 'border-ink text-text-primary' : 'border-transparent text-text-muted hover:text-text-primary'"
          role="tab"
          :aria-selected="tab === t"
          :data-testid="`server-tab-${t}`"
          @click="tab = t"
        >
          {{ t }}
        </button>
      </nav>
    </header>

    <AdminReports v-if="tab === 'moderation'" />
    <template v-else>
    <form v-if="saved" class="grid border-b border-border md:grid-cols-2" data-testid="server-settings" @submit.prevent="save">
      <section class="border-border px-6 py-5 max-md:px-4 max-md:border-b md:border-r">
        <h2 class="label">General</h2>
        <label class="label mt-3 text-text-muted" for="server-name-input">Server name</label>
        <input id="server-name-input" v-model="form.name" class="input" maxlength="64" data-testid="server-name-input" />
        <p class="mono mt-2 text-text-muted">Shown on invites and sign-in screens.</p>
        <label class="label mt-4 text-text-muted" for="max-attachment">Largest attachment</label>
        <div class="flex items-center gap-3">
          <input
            id="max-attachment"
            v-model.number="form.max_attachment_mb"
            type="number"
            min="1"
            :max="form.attachment_ceiling_mb"
            class="input w-24"
            data-testid="max-attachment"
          />
          <span class="mono text-text-muted">MB · up to {{ form.attachment_ceiling_mb }} on this server</span>
        </div>
        <label class="label mt-4 text-text-muted" for="max-repository">Largest repository</label>
        <div class="flex items-center gap-3">
          <input
            id="max-repository"
            v-model.number="form.max_repository_mb"
            type="number"
            min="0"
            class="input w-24"
            data-testid="max-repository"
          />
          <span class="mono text-text-muted">MB · 0 = no limit</span>
        </div>
        <label class="label mt-4 text-text-muted" for="max-repositories">All repositories together</label>
        <div class="flex items-center gap-3">
          <input
            id="max-repositories"
            v-model.number="form.max_repositories_total_mb"
            type="number"
            min="0"
            class="input w-24"
            data-testid="max-repositories"
          />
          <span class="mono text-text-muted">MB · 0 = no limit</span>
        </div>
      </section>
      <section class="px-6 py-5 max-md:px-4">
        <h2 class="label">Spaces</h2>
        <div class="label mt-3 text-text-muted">Who can create spaces</div>
        <div class="space-y-1.5" role="radiogroup" aria-label="Who can create spaces">
          <label v-for="p in policies" :key="p.value" class="flex cursor-pointer items-start gap-2.5 text-sm">
            <input
              v-model="form.space_creation"
              type="radio"
              name="space-creation"
              class="mt-1"
              :value="p.value"
              :data-testid="`space-creation-${p.value}`"
            />
            <span>
              {{ p.label }}
              <span class="block text-xs text-text-muted">{{ p.hint }}</span>
            </span>
          </label>
        </div>
        <label class="label mt-4 text-text-muted" for="max-party">Largest watch party or listening room</label>
        <div class="flex items-center gap-3">
          <input id="max-party" v-model.number="form.max_party_size" type="number" min="2" max="1000" class="input w-24" data-testid="max-party" />
          <span class="mono text-text-muted">people · watching together is for friends</span>
        </div>
        <label class="label mt-4 text-text-muted" for="large-space">Libraries and parties up to</label>
        <div class="flex items-center gap-3">
          <input id="large-space" v-model.number="form.large_space_members" type="number" min="0" class="input w-24" data-testid="large-space" />
          <span class="mono text-text-muted">members in a space · 0 = any size</span>
        </div>
        <label class="label mt-4 text-text-muted" for="max-spaces">Spaces each person may own</label>
        <div class="flex items-center gap-3">
          <input
            id="max-spaces"
            v-model.number="form.max_spaces_per_user"
            type="number"
            min="0"
            max="1000"
            class="input w-24"
            data-testid="max-spaces"
          />
          <span class="mono text-text-muted">0 = no limit · admins aren't limited</span>
        </div>
      </section>
      <section class="border-t border-border px-6 py-5 max-md:px-4 md:col-span-2">
        <h2 class="label">Notifications</h2>
        <label class="flex cursor-pointer items-start gap-3 text-sm">
          <input v-model="form.push_notifications" type="checkbox" class="mt-0.5" data-testid="allow-push" />
          <span>
            Push notifications
            <span class="mono block text-text-muted">
              Lets people get notified while the app is closed. Pushes travel through the push service of each
              person's browser (Apple, Google, Mozilla or Microsoft), encrypted: they see only that a notification was
              sent and when, never who it's from or what it says. Off: notifications only while the app is open.
            </span>
          </span>
        </label>
      </section>
      <section class="border-t border-border px-6 py-5 max-md:px-4 md:col-span-2" data-testid="member-storage-settings">
        <h2 class="label">Members' own storage</h2>
        <label class="flex cursor-pointer items-start gap-3 text-sm">
          <input v-model="form.member_storage" type="checkbox" class="mt-0.5" data-testid="allow-member-storage" />
          <span>
            Let members attach their own storage
            <span class="mono block text-text-muted">
              Members can attach an S3 bucket in their own account or add a node (a computer of theirs serving
              folders) in Settings, create libraries there, and open their nodes to others. Those libraries are theirs:
              they share them and choose who adds files. You can still unshare one or remove it from this server, but
              its files stay in their storage. This server never holds their storage's keys or addresses.
            </span>
          </span>
        </label>
        <div v-if="form.member_storage" class="mt-3 flex flex-wrap items-center gap-3">
          <input
            id="max-locations"
            v-model.number="form.max_locations_per_user"
            type="number"
            min="0"
            max="100"
            class="input w-20"
            aria-label="Buckets and nodes each person may attach"
            data-testid="max-locations"
          />
          <span class="mono text-text-muted">buckets and nodes, and</span>
          <input
            id="max-libraries"
            v-model.number="form.max_libraries_per_user"
            type="number"
            min="0"
            max="1000"
            class="input w-20"
            aria-label="Libraries each person may own"
            data-testid="max-libraries"
          />
          <span class="mono text-text-muted">libraries each · 0 = no limit · admins aren't limited</span>
        </div>
      </section>
      <section class="border-t border-border px-6 py-5 max-md:px-4 md:col-span-2" data-testid="palette-editor">
        <h2 class="label">Icons and profile pictures</h2>
        <label class="mb-5 flex cursor-pointer items-start gap-3 text-sm">
          <input v-model="form.profile_photos" type="checkbox" class="mt-0.5" data-testid="allow-profile-photos" />
          <span>
            Allow profile photos
            <span class="mono block text-text-muted">
              Off: everyone uses a designed picture (or their generated mark), and no photos are shown, from this
              server or others. Photos already uploaded are kept and come back if you turn this on again.
            </span>
          </span>
        </label>
        <h3 class="label">Palette · {{ String(form.palette.length).padStart(2, '0') }}</h3>
        <p class="mono mb-3 text-text-muted">
          Colours people can use for designed pictures and space symbols (2–16), after monochrome, which is always
          there and shows white on dark and black on light. Any colour works: where one lacks 3:1 contrast on a
          theme it's drawn lightened (dark) or deepened (light) just enough, as each swatch shows (dark half,
          light half). Removing a colour moves existing designs to the nearest colour left.
        </p>
        <div class="flex flex-wrap items-center gap-2">
          <!-- Always first, can't be removed: white on the dark theme, black on the light. -->
          <span
            class="block h-8 w-8 border border-border"
            style="background: linear-gradient(135deg, #fff 50%, #000 50%)"
            title="Monochrome: white on dark, black on light. Always available."
            data-testid="palette-ink"
          />
          <span v-for="c in form.palette" :key="c" class="group relative" :data-testid="`palette-${c}`">
            <span class="block h-8 w-8 border border-border" :style="swatch(c)" :title="c" />
            <button
              type="button"
              class="mono absolute -top-1.5 -right-1.5 hidden h-4 w-4 items-center justify-center bg-bg-elevated text-[10px] text-text-muted group-hover:flex hover:text-signal"
              :disabled="form.palette.length <= 2"
              :aria-label="`Remove ${c}`"
              @click="removeColor(c)"
            >
              ✕
            </button>
          </span>
          <span class="ml-2 flex items-center gap-2">
            <input v-model="newColor" type="color" class="h-8 w-8 cursor-pointer border border-border bg-transparent p-0" aria-label="New colour" data-testid="palette-new" />
            <button type="button" class="btn-ghost px-2" :disabled="form.palette.length >= 16" data-testid="palette-add" @click="addColor">+ Add</button>
            <span class="block h-8 w-8 border border-border" :style="swatch(newColor.toLowerCase())" title="As drawn: dark half, light half" />
            <span class="mono text-text-muted uppercase" data-testid="palette-contrast">
              {{
                newAdjusted.dark ? 'lightened on dark' : newAdjusted.light ? 'deepened on light' : 'as is on both themes'
              }}
            </span>
          </span>
        </div>
      </section>
      <div class="flex justify-end gap-2 border-t border-border px-6 py-3 max-md:px-4 md:col-span-2">
        <button class="btn-primary" :disabled="busy || !dirty || !form.name.trim()" data-testid="save-server-settings">Save</button>
      </div>
    </form>

    <UpdateSettings />

    <FontSettings />

    <AttachmentSettings />

    <section class="px-6 py-5 max-md:px-4" data-testid="server-people">
      <div class="mb-3 flex items-center gap-3">
        <h2 class="label mb-0 shrink-0">People · {{ pad(users.length, 2) }}</h2>
        <input
          v-model="query"
          type="search"
          class="input h-7 min-w-0 max-w-72 flex-1 py-0 text-xs"
          placeholder="Search user@host or name"
          aria-label="Search people"
          data-testid="server-people-search"
        />
      </div>
      <div class="mono grid grid-cols-[1fr_5rem_7rem_4rem] gap-3 border-b border-border-strong pb-1.5 text-text-muted uppercase max-sm:grid-cols-[1fr_4rem_5rem]">
        <span>Account</span>
        <span>Admin</span>
        <span>Create spaces</span>
        <span class="text-right max-sm:hidden">Owns</span>
      </div>
      <p v-if="shown.length === 0" class="mono py-2 text-text-muted">No one matches.</p>
      <div
        v-for="u in shown"
        :key="u.user_id"
        class="grid grid-cols-[1fr_5rem_7rem_4rem] items-center gap-3 border-b border-border py-2 text-sm max-sm:grid-cols-[1fr_4rem_5rem]"
        :data-testid="`account-${u.display_name}`"
      >
        <span class="min-w-0 leading-tight">
          <span class="block truncate">{{ u.user_id }}<span v-if="u.user_id === session.me" class="mono ml-1.5 text-text-disabled">you</span></span>
          <span class="block truncate text-xs text-text-muted">{{ u.display_name }}</span>
        </span>
        <span>
          <input
            type="checkbox"
            :checked="u.is_admin"
            :disabled="busy || u.user_id === session.me"
            :title="u.user_id === session.me ? 'Ask another admin to remove your admin rights' : undefined"
            :aria-label="`${u.user_id} is an admin`"
            data-testid="account-admin"
            @change="setFlag(u, 'is_admin', ($event.target as HTMLInputElement).checked)"
          />
        </span>
        <span>
          <input
            type="checkbox"
            :checked="u.is_admin || form.space_creation === 'everyone' || u.can_create_spaces"
            :disabled="busy || u.is_admin || saved?.space_creation === 'everyone'"
            :title="u.is_admin ? 'Admins can always create spaces' : saved?.space_creation === 'everyone' ? 'Everyone can create spaces' : undefined"
            :aria-label="`${u.user_id} can create spaces`"
            data-testid="account-create-spaces"
            @change="setFlag(u, 'can_create_spaces', ($event.target as HTMLInputElement).checked)"
          />
        </span>
        <span class="mono text-right text-text-muted max-sm:hidden">{{ pad(u.spaces_owned, 2) }}</span>
      </div>
    </section>
    </template>
  </main>
</template>
